import { Link, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useBranding } from "@/lib/branding";
import { useCapabilities } from "@/lib/capabilities";
import { useTheme } from "@/lib/theme";
import { Icon, type IconName } from "./Icon";
import { Avatar } from "./Avatar";
import logoMark from "@/assets/logo-mark.svg";
import type { ProjectRow } from "@shared/types";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: number;
}

const PRIMARY_NAV: NavItem[] = [
  { to: "/", label: "Home", icon: "home" },
  { to: "/dashboards", label: "Dashboards", icon: "grid" },
  { to: "/my-tasks", label: "My Tasks", icon: "check" },
  { to: "/inbox", label: "Notifications", icon: "bell" },
  { to: "/activity", label: "Activity", icon: "clock" },
  { to: "/roadmap", label: "Roadmap", icon: "arrow" },
  { to: "/portfolio", label: "Portfolio", icon: "chev" },
  { to: "/files", label: "Files", icon: "folder" },
  { to: "/team", label: "Team", icon: "users" },
  { to: "/capacity", label: "Capacity", icon: "grid" },
  { to: "/workload", label: "Workload", icon: "board" },
  { to: "/allocations", label: "Allocation", icon: "cal" },
];

export function Sidebar({
  onNavigate,
  collapsed = false,
  onCollapse,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
  onCollapse?: () => void;
} = {}) {
  const { orgName, logoUrl } = useBranding();
  const { attachments } = useCapabilities();
  const { theme, toggle } = useTheme();
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const qc = useQueryClient();
  const projects = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const me = useQuery({
    queryKey: queryKeys.me(),
    queryFn: api.me,
    retry: false,
  });

  // Drag-to-reorder for the sidebar project list. We mirror the optimistic
  // pattern from KanbanBoard: write the new order into the React Query cache
  // synchronously on drop, then fire the PATCH. On error we rollback by
  // invalidating so the server's truth re-renders.
  const sortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderProjects(orderedIds),
    onError: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const current = projects.data?.projects ?? [];
    const oldIndex = current.findIndex((p) => p.id === active.id);
    const newIndex = current.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(current, oldIndex, newIndex);

    // Optimistic update — write the new order into the cache so the sidebar
    // re-renders immediately. Other surfaces sharing this query key (the
    // /projects index) also pick up the new order without a refetch.
    qc.setQueryData<{ projects: ProjectRow[] }>(queryKeys.projects(), (old) =>
      old ? { ...old, projects: next } : old,
    );
    reorder.mutate(next.map((p) => p.id));
  }

  return (
    <aside
      className={`sidebar bui${collapsed ? " collapsed" : ""}`}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <div className="sb-brand">
        <div className="mk">
          <img src={logoUrl || logoMark} alt={orgName} />
        </div>
        <div>
          <b>{orgName}</b>
          <span
            className="sb-build"
            title={`build #${__BUILD_COUNT__} · ${__BUILD_SHA__}`}
          >
            v{__BUILD_COUNT__} · {__BUILD_SHA__}
          </span>
        </div>
        {onCollapse ? (
          <button
            type="button"
            className="sb-collapse"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            onClick={onCollapse}
          >
            <Icon name="chev" size={16} />
          </button>
        ) : (
          <div className="ws-switch" aria-hidden="true">
            <Icon name="chev" size={16} />
          </div>
        )}
      </div>
      <div className="sb-scroll">
        <div className="sb-search input-search">
          <Icon name="search" />
          {/* Styled as an input but really a button that opens the global
              CommandPalette (owned by TopBar). Cmd/Ctrl-K still works. */}
          <button
            type="button"
            className="input"
            aria-label="Search or jump to (⌘K)"
            title="Search (⌘K)"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("command-palette:open"))
            }
            style={{
              textAlign: "left",
              cursor: "pointer",
              background: "transparent",
              color: "var(--muted)",
            }}
          >
            Search or jump to…
          </button>
        </div>
        <div className="sb-group">
          {PRIMARY_NAV.filter((n) => attachments || n.to !== "/files").map(
            (n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`nav-item${currentPath === n.to ? " active" : ""}`}
                onClick={onNavigate}
              >
                <Icon name={n.icon} />
                {n.label}
                {n.badge !== undefined && (
                  <span className="ni-badge">
                    <span className="badge-count">{n.badge}</span>
                  </span>
                )}
              </Link>
            ),
          )}
        </div>
        <div className="sb-group">
          <div className="sb-group-h">
            Projects
            <Link to="/projects/new" className="add" aria-label="New project">
              <Icon name="plus" size={13} />
            </Link>
          </div>
          <DndContext
            sensors={sortSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={(projects.data?.projects ?? []).map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {(projects.data?.projects ?? []).map((p) => (
                <SortableProjectItem
                  key={p.id}
                  project={p}
                  active={currentPath.startsWith(`/projects/${p.id}`)}
                  onNavigate={onNavigate}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Link to="/projects" className="nav-item" onClick={onNavigate}>
            <Icon name="folder" />
            All projects
          </Link>
        </div>
      </div>
      <div className="sb-foot">
        {me.data?.user.role === "admin" && (
          <Link
            to="/admin/users"
            className={`nav-item${
              currentPath.startsWith("/admin") ? " active" : ""
            }`}
            onClick={onNavigate}
          >
            <Icon name="key" />
            Admin
          </Link>
        )}
        <Link
          to="/settings/appearance"
          className={`nav-item${
            currentPath.startsWith("/settings") ? " active" : ""
          }`}
          onClick={onNavigate}
        >
          <Icon name="settings" />
          Settings
        </Link>
        <div className="sb-user">
          <Link to="/settings/account" className="sb-user-link">
            {me.data && (
              <Avatar
                id={me.data.user.id}
                name={me.data.user.name || me.data.user.email}
                size="md"
                image={me.data.user.image}
              />
            )}
            <div>
              <div className="nm">
                {me.data?.user.name || me.data?.user.email || "Guest"}
              </div>
              <div className="rl">
                {me.data?.user.role === "admin" ? "Admin" : "Member"}
              </div>
            </div>
          </Link>
          <button
            className="theme-mini"
            onClick={toggle}
            title="Toggle theme"
            aria-label="Toggle theme"
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * One sortable row in the sidebar's project list.
 *
 * The dnd-kit listeners are attached to the link itself (not a dedicated
 * drag handle) so the whole row is draggable. The PointerSensor's 6px
 * activation distance is what disambiguates clicks (navigate) from drags
 * (reorder) — a normal click won't move the pointer that far before
 * mouseup, so navigation still works.
 */
function SortableProjectItem({
  project,
  active,
  onNavigate,
}: {
  project: ProjectRow;
  active: boolean;
  onNavigate?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <Link
      ref={setNodeRef}
      style={style}
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      className={`nav-item${active ? " active" : ""}`}
      onClick={onNavigate}
      {...attributes}
      {...listeners}
    >
      <span className="proj-dot" style={{ background: "var(--green-500)" }} />
      {project.name}
    </Link>
  );
}
